import React from 'react';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { formatNumber } from 'libs/end-to-end/src/number-formatting';
import { formatCurrency } from 'apps/sigma2-ui/src/lib/number-formatting';
import { KeyValuePair } from 'apps/sigma2-ui/src/components/ui/KeyValuePair';
import { Value } from 'apps/sigma2-ui/src/components/ui/KeyValuePair';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { Percentage } from 'apps/sigma2-ui/src/components/Percentage';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';

export interface PayCorrectionCompensationProps {
  numAcceptable: number;
  numNeedsAttention: number;
}

export interface PayCorrectionBudgetProps {
  recommended: number;
  actual: number;
}

export interface PayCorrectionImpactProps {
  payGapPercentage: number;
}

export interface PayCorrectionProps {
  numEmployees: number;
  payGapPercentage: number;
  compensation: PayCorrectionCompensationProps;
  budget: PayCorrectionBudgetProps;
  impact: PayCorrectionImpactProps;
}

interface PayCorrectionSidebarProps extends PayCorrectionProps {
  onAcceptableListCardClick: () => void;
  onNeedsAttentionListCardClick: () => void;
  onAllEmployeesListCardClick: () => void;
  todaysPayGapTooltipContents: React.ReactNode;
}

const TodaysPayGapTitle = ({
  tooltipContents,
}: {
  tooltipContents: React.ReactNode;
}) => {
  return (
    <Spacing size={'sm'} direction={'horizontal'}>
      {`Today's Pay Gap`}{' '}
      <LightTooltip placement="right-start" title={tooltipContents}>
        <span aria-label={`Today's Pay Gap Information Icon`}>
          <IconInformationSvg svg={{ className: 'relative w-4 h-4' }} />
        </span>
      </LightTooltip>
    </Spacing>
  );
};

export const PayCorrectionSidebar: React.FC<PayCorrectionSidebarProps> = ({
  numEmployees,
  payGapPercentage,
  compensation,
  budget,
  impact,
  onAcceptableListCardClick,
  onNeedsAttentionListCardClick,
  onAllEmployeesListCardClick,
  todaysPayGapTooltipContents,
}) => {
  return (
    <Spacing
      direction="vertical"
      size="md"
      className="w-full max-w-xs bg-gray-50 py-10 px-6 rounded-lg"
    >
      <button onClick={onAllEmployeesListCardClick} className="w-full">
        <KeyValuePair title="Employees">
          <Value variant="lg">{formatNumber(numEmployees)}</Value>
        </KeyValuePair>
      </button>

      <KeyValuePair
        title={
          <TodaysPayGapTitle tooltipContents={todaysPayGapTooltipContents} />
        }
      >
        <Value>{payGapPercentage.toFixed(1)}%</Value>
      </KeyValuePair>

      <Spacing direction="vertical" size="md">
        <Spacing direction="vertical" size="sm">
          <Heading
            level={3}
            textColor="text-secondary"
            fontFamily="font-sans"
            fontWeight="font-bold"
          >
            Compensation
          </Heading>

          <button onClick={onAcceptableListCardClick}>
            <FlatCard className="bg-white hover:bg-blue-800 hover:bg-opacity-5 transition-colors duration-300 ease-in-out">
              <KeyValuePair title="Acceptable">
                <Value>{formatNumber(compensation.numAcceptable)}</Value>
              </KeyValuePair>
            </FlatCard>
          </button>

          <button onClick={onNeedsAttentionListCardClick}>
            <FlatCard className="bg-white hover:bg-blue-800 hover:bg-opacity-5 transition-colors duration-300 ease-in-out">
              <KeyValuePair title="Needs Attention">
                <Value>{formatNumber(compensation.numNeedsAttention)}</Value>
              </KeyValuePair>
            </FlatCard>
          </button>
        </Spacing>

        <FlatCard className="bg-blue-800 bg-opacity-5">
          <Spacing direction="vertical" size="md">
            <div>
              <Heading
                level={3}
                textColor="text-secondary"
                fontFamily="font-sans"
                fontWeight="font-bold"
              >
                Budget & Impact
              </Heading>

              <Paragraph
                className="text-xs font-medium"
                textColor="text-gray-900"
              >
                Recommended budget:{' '}
                <span className="font-bold">
                  {formatCurrency(budget.recommended)}
                </span>
              </Paragraph>
            </div>

            <KeyValuePair title="Budget">
              <Value>{formatCurrency(budget.actual)}</Value>
            </KeyValuePair>

            <KeyValuePair title="Projected Pay Gap">
              <Percentage percentage={impact.payGapPercentage} />
            </KeyValuePair>
          </Spacing>
        </FlatCard>
      </Spacing>
    </Spacing>
  );
};
